import { TranslocoPipe } from '@jsverse/transloco';
import { ChangeDetectionStrategy, Component, inject, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LucideCircleCheck, LucideInfo, LucideOctagonAlert, LucideTriangleAlert, LucideX } from '@lucide/angular';
import { AppStateService } from '../../../core/app-state/app-state.service';

/**
 * Renders the notification feed from {@link AppStateService}. Shared so the desktop sidebar and the
 * mobile "More" sheet present the same list from one source. Emits `navigated` when a notification
 * link is followed, letting the host dismiss whatever chrome contains it.
 *
 * Tones share the toast host's vocabulary: the same Lucide icons and semantic color tokens, so the
 * two notification surfaces read as one system.
 */
@Component({
  selector: 'app-notification-list',
  standalone: true,
  imports: [TranslocoPipe, RouterLink, LucideCircleCheck, LucideInfo, LucideOctagonAlert, LucideTriangleAlert, LucideX],
  template: `
    <div class="notif-list" role="region" [attr.aria-label]="'core.notifications' | transloco">
      @for (item of state.notifications(); track item.id) {
        <article [class]="item.tone">
          <span class="tone-icon" aria-hidden="true">
            @switch (item.tone) {
              @case ('success') { <svg lucideCircleCheck size="14"></svg> }
              @case ('warning') { <svg lucideTriangleAlert size="14"></svg> }
              @case ('danger') { <svg lucideOctagonAlert size="14"></svg> }
              @default { <svg lucideInfo size="14"></svg> }
            }
          </span>
          @if (item.link) {
            <a [routerLink]="item.link" (click)="navigated.emit()">
              <strong>{{ item.title }}</strong>
              <span>{{ item.body }}</span>
            </a>
          } @else {
            <div>
              <strong>{{ item.title }}</strong>
              <span>{{ item.body }}</span>
            </div>
          }
          <button type="button" (click)="state.dismissNotification(item.id)" [attr.aria-label]="'core.dismiss' | transloco">
            <svg lucideX size="14" aria-hidden="true"></svg>
          </button>
        </article>
      } @empty {
        <p class="muted">{{ 'core.noNotifications' | transloco }}</p>
      }
    </div>
  `,
  styles: [`
    .notif-list {
      display: grid;
      gap: var(--space-1);
      max-height: 18rem;
      overflow-y: auto;
      overscroll-behavior: contain;
      padding: var(--space-1) 0;
    }
    /* Quiet rows: the tone lives only in the icon chip; the row itself stays neutral and only
       lights up on hover, so a full feed doesn't read as a wall of colored boxes. */
    .notif-list article {
      --tone-color: var(--color-info);
      align-items: center;
      border-radius: var(--radius-lg);
      display: grid;
      gap: var(--space-3);
      grid-template-columns: auto minmax(0, 1fr) auto;
      padding: var(--space-2);
      transition: background var(--motion-fast) ease;
    }
    .notif-list article:hover,
    .notif-list article:focus-within { background: var(--color-surface-high); }
    .notif-list article.success { --tone-color: var(--color-success); }
    .notif-list article.warning { --tone-color: var(--color-warning); }
    .notif-list article.danger { --tone-color: var(--color-danger); }

    .tone-icon {
      align-items: center;
      background: color-mix(in srgb, var(--tone-color) 13%, transparent);
      border-radius: 50%;
      color: var(--tone-color);
      display: inline-flex;
      flex: none;
      height: 2rem;
      justify-content: center;
      width: 2rem;
    }

    .notif-list a,
    .notif-list article > div { display: grid; gap: 0.05rem; min-width: 0; text-decoration: none; }
    .notif-list strong {
      color: var(--color-text-primary);
      font-size: var(--font-size-caption);
      font-weight: 800;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .notif-list span {
      color: var(--color-text-secondary);
      font-size: var(--font-size-micro);
      line-height: 1.35;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .notif-list article > button {
      align-items: center;
      background: none;
      border: 0;
      border-radius: 50%;
      color: var(--color-text-secondary);
      cursor: pointer;
      display: inline-flex;
      flex: 0 0 auto;
      height: 1.7rem;
      justify-content: center;
      line-height: 1;
      padding: 0;
      transition: background var(--motion-fast) ease, color var(--motion-fast) ease, opacity var(--motion-fast) ease;
      width: 1.7rem;
    }
    /* On pointer devices the dismiss control stays out of the way until the row is hovered. */
    @media (hover: hover) {
      .notif-list article > button { opacity: 0; }
      .notif-list article:hover > button,
      .notif-list article:focus-within > button { opacity: 1; }
    }
    .notif-list article > button:hover {
      background: color-mix(in srgb, var(--color-text-primary) 10%, transparent);
      color: var(--color-text-primary);
    }
    .notif-list article > button:focus-visible {
      opacity: 1;
      outline: var(--focus-ring);
      outline-offset: 1px;
    }
    .notif-list .muted { color: var(--color-text-secondary); font-size: var(--font-size-caption); margin: 0; padding: var(--space-2) var(--space-3); }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationListComponent {
  readonly state = inject(AppStateService);
  readonly navigated = output<void>();
}
